import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Refrigerator, ShoppingBasket, Snowflake } from "lucide-react";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const URL_SELF = "https://vendingmachinefree.com/free-micro-market-placement";
const TITLE = "Free Micro-Market & Smart-Fridge Placement for Offices | $0 Install";
const DESC =
  "Free micro-market and smart-fridge placement for US offices — fresh food, premium coffee, cold-brew, healthy snacks. $0 install, $0 service, no contracts. Ideal for 25+ employee tech offices.";

const FAQS = [
  {
    q: "What is a free micro-market?",
    a: "A self-checkout mini convenience store placed inside your office at $0 cost. Open shelving for snacks, coolers for fresh food and beverages, and a tap-to-pay kiosk. We supply, install, stock, insure, and service everything — our revenue comes from product sales.",
  },
  {
    q: "How is a smart fridge different from a micro-market?",
    a: "A smart fridge is a self-locking cooler with tap-to-pay — open the door, take what you want, it charges you on close. Roughly 3×3 ft footprint. Ideal for 20–75 person offices. Micro-markets are larger (8×3 ft+) and fit 75+ person offices or shared building lobbies.",
  },
  {
    q: "What size office qualifies?",
    a: "Smart fridges qualify from ~20 employees. Full micro-markets typically need 40+ on-site staff, or 100+ shared building traffic. We size the placement to your headcount and shift pattern during a free site survey.",
  },
  {
    q: "Do you carry fresh food and premium coffee?",
    a: "Yes. Micro-markets and smart fridges routinely stock fresh salads, wraps, cold-pressed juice, oat-milk cold-brew, energy drinks, and healthier snacks. You can request allergen labeling and dietary-mix preferences.",
  },
  {
    q: "How fast is install?",
    a: "Most sites are surveyed within 48 hours and fully installed within 1–2 weeks of approval. Zero cost to your business at any point.",
  },
];

export const Route = createFileRoute("/free-micro-market-placement")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_SELF },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Free Micro-Market & Smart-Fridge Placement",
          serviceType: "Free Micro-Market Placement",
          provider: { "@type": "Organization", name: "Vending Machine Free.com" },
          areaServed: { "@type": "Country", name: "United States" },
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: URL_SELF,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("micro_market_header")} className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground">
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <ShoppingBasket className="h-4 w-4" /> Micro-Markets · Smart Fridges
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Micro-Market & Smart-Fridge Placement
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            Fresh food, cold-brew, premium coffee, and healthier snacks — inside your office at $0 cost.
            Ideal for 25+ employee tech offices, corporate towers, and shared building lobbies.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("micro_market_hero")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Get a Free Micro-Market Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-6xl">
          <h2 className="font-display text-2xl uppercase">Two Placement Options — Both $0</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <div className="rounded-xl border border-border bg-card p-6">
              <Refrigerator className="h-9 w-9 text-safety" />
              <h3 className="mt-3 font-display text-xl uppercase">Smart Fridge</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Self-locking cooler with tap-to-pay. Open the door, take what you want, it charges you on close.
                ~3×3 ft footprint. Ideal for 20–75 person offices and startup floors.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-safety flex-shrink-0" /> Fresh salads, wraps, cold-brew</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-safety flex-shrink-0" /> Tap-to-pay, no line</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-safety flex-shrink-0" /> Standard 120V outlet</li>
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <ShoppingBasket className="h-9 w-9 text-safety" />
              <h3 className="mt-3 font-display text-xl uppercase">Full Micro-Market</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Mini convenience store with open shelving, coolers, freezers, and self-checkout kiosk.
                ~8×3 ft minimum. Ideal for 75+ employee offices and shared multi-tenant buildings.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-safety flex-shrink-0" /> 200–400 SKUs incl. fresh food</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-safety flex-shrink-0" /> Specialty coffee & cold-brew</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-safety flex-shrink-0" /> Weekly restocks + monthly cleaning</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-secondary">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-2xl uppercase">Best-Fit Offices</h2>
          <p className="mt-3 text-muted-foreground">
            Micro-markets and smart fridges are engineered for offices where employees expect Class-A breakroom
            amenities — tech HQs, engineering teams, financial services, creative agencies, and multi-tenant towers.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
            {[
              "IT & software teams (odd hours, 24/7 build cycles)",
              "Financial services & professional services",
              "Media, creative & advertising agencies",
              "Life-sciences & biotech offices",
              "Shared multi-tenant building lobbies",
              "Coworking floors with 40+ headcount",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2 rounded-md bg-card border border-border p-3">
                <Snowflake className="h-5 w-5 text-safety flex-shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">Micro-Market FAQs</h2>
          <div className="mt-8 divide-y divide-border border border-border rounded-xl bg-card">
            {FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-charcoal text-white text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Apply for a Free Micro-Market</h2>
          <p className="mt-3 opacity-80">Local operator response in 24 hours. Zero cost, no contract.</p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("micro_market_footer")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground"
          >
            Get Started <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-8 text-sm">
            Explore <Link to="/hubs" className="text-safety hover:underline">tech-hub coverage</Link> ·{" "}
            <Link to="/free-office-vending-machine-placement" className="text-safety hover:underline">free office vending</Link> ·{" "}
            <Link to="/free-vending-machines-usa" className="text-safety hover:underline">USA overview</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
